import React, { createContext, useContext, useReducer } from 'react';
import { AdminUser } from '../types';

interface AdminState {
  currentUser: AdminUser | null;
  isAuthenticated: boolean;
  currentPage: string;
}

type AdminAction =
  | { type: 'SET_USER'; payload: AdminUser }
  | { type: 'LOGIN'; payload: { user: AdminUser; token: string } }
  | { type: 'LOGOUT' }
  | { type: 'SET_PAGE'; payload: string };

const adminReducer = (state: AdminState, action: AdminAction): AdminState => {
  switch (action.type) {
    case 'LOGIN':
      // Store token in localStorage
      localStorage.setItem('token', action.payload.token);
      return {
        ...state,
        currentUser: action.payload.user,
        isAuthenticated: true,
      };
    case 'LOGOUT':
      // Clear token from localStorage
      localStorage.removeItem('token');
      return {
        ...state,
        currentUser: null,
        isAuthenticated: false,
      };
    case 'SET_USER':
      return {
        ...state,
        currentUser: action.payload,
        isAuthenticated: true,
      };
    case 'SET_PAGE':
      return {
        ...state,
        currentPage: action.payload,
      };
    default:
      return state;
  }
};

const AdminContext = createContext<{
  state: AdminState;
  dispatch: React.Dispatch<AdminAction>;
} | null>(null);

export const AdminProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(adminReducer, {
    currentUser: null,
    isAuthenticated: Boolean(localStorage.getItem('token')), // Check if token exists
    currentPage: '',
  });

  return (
    <AdminContext.Provider value={{ state, dispatch }}>
      {children}
    </AdminContext.Provider>
  );
};

export const useAdmin = () => {
  const context = useContext(AdminContext);
  if (!context) {
    throw new Error('useAdmin must be used within an AdminProvider');
  }
  return context;
};